// What the app remembers between sessions: how you like to play, and how well
// you have played. The run itself lives in the engine and is deliberately not
// here — a game in progress is not state worth persisting.

import { ref, watch } from 'vue';
import { prefs } from './core/storage.js';
import {
  DIFFICULTIES, difficultyById, MAX_GHOSTS, MAX_EXTRA_PIES, SPEEDS, speedById,
} from './game/ai.js';
import { BOARDS, boardById } from './game/maze.js';

export {
  DIFFICULTIES, difficultyById, BOARDS, boardById, MAX_GHOSTS, MAX_EXTRA_PIES,
  SPEEDS, speedById,
};

const stored = (key, fallback) => (prefs[key] === undefined ? fallback : prefs[key]);

export const difficulty = ref(stored('difficulty', 'hungry'));
export const soundOn = ref(stored('sound', true));
export const padOn = ref(stored('pad', true));

/** Best score and deepest level, kept per difficulty — they are not comparable. */
/** How the next run is set up. Changing any of these does not touch one in progress. */
export const boardId = ref(stored('board', 'standard'));
export const ghostCount = ref(stored('ghosts', 1));
export const extraPies = ref(stored('extraPies', 0));
export const speed = ref(stored('speed', 'normal'));

/** Basil and olive instead of pepperoni. Cosmetic; the pizza scores the same. */
export const vegan = ref(stored('vegan', false));

/**
 * What the two characters are called.
 *
 * The app is still PizzaRaf and the How to play screen still says Nick and Raf —
 * those are the product. These are what the people playing want to call them,
 * and they only ever appear where the game is talking about a character.
 */
export const NAME_MAX = 12;
export const DEFAULT_NAMES = Object.freeze({ nick: 'Nick', raf: 'Raf' });
export const names = ref({ ...DEFAULT_NAMES, ...(stored('names', {}) || {}) });

export function setName(who, value) {
  const clean = String(value ?? '').replace(/\s+/g, ' ').trim().slice(0, NAME_MAX);
  // Blank falls back rather than sticking: a nameless character reads as a bug,
  // and there is nowhere in the UI that an empty label would make sense.
  names.value = { ...names.value, [who]: clean || DEFAULT_NAMES[who] };
}

export const resetNames = () => { names.value = { ...DEFAULT_NAMES }; };

/** "Raf" -> "Rafs", but "Bess" -> "Bess" rather than "Besss". */
export const plural = (name) => (/s$/i.test(name) ? name : `${name}s`);

/**
 * How big each uploaded picture is drawn, as a multiple of the drawn character.
 *
 * Separate for Nick and Raf, because a cut-out head and a square snapshot want
 * different framing and no single number suits both. They START equal, which is
 * the part that was actually broken: Raf's picture used to be blitted at three
 * and a half times Nick's.
 */
export const FACE_SCALE_MIN = 0.5;
export const FACE_SCALE_MAX = 2.5;
export const FACE_SCALE_STEP = 0.05;
export const FACE_SCALE_DEFAULT = 1;
export const faceScales = ref({
  nick: FACE_SCALE_DEFAULT, raf: FACE_SCALE_DEFAULT, ...(stored('faceScales', {}) || {}),
});

export const best = ref(stored('best', {}));

watch(difficulty, (v) => { prefs.difficulty = v; });
watch(soundOn, (v) => { prefs.sound = v; });
watch(padOn, (v) => { prefs.pad = v; });
watch(boardId, (v) => { prefs.board = v; });
watch(ghostCount, (v) => { prefs.ghosts = v; });
watch(extraPies, (v) => { prefs.extraPies = v; });
watch(speed, (v) => { prefs.speed = v; });
watch(vegan, (v) => { prefs.vegan = v; });
watch(names, (v) => { prefs.names = v; }, { deep: true });
watch(faceScales, (v) => { prefs.faceScales = v; }, { deep: true });
watch(best, (v) => { prefs.best = v; }, { deep: true });

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, Math.round(Number(n) || 0)));
export const setGhosts = (n) => { ghostCount.value = clamp(n, 1, MAX_GHOSTS); };
export const setExtraPies = (n) => { extraPies.value = clamp(n, 0, MAX_EXTRA_PIES); };
export function setFaceScale(who, n) {
  const v = Number(n);
  const clamped = Math.max(FACE_SCALE_MIN, Math.min(FACE_SCALE_MAX, Number.isFinite(v) ? v : 1));
  faceScales.value = { ...faceScales.value, [who]: Math.round(clamped * 100) / 100 };
}

export function resetFaceScales() {
  faceScales.value = { nick: FACE_SCALE_DEFAULT, raf: FACE_SCALE_DEFAULT };
}

export const scalesAreDefault = () => (
  faceScales.value.nick === FACE_SCALE_DEFAULT && faceScales.value.raf === FACE_SCALE_DEFAULT
);

/** Everything createGame() needs, in one place. */
export const setup = () => ({
  difficulty: difficulty.value,
  board: boardId.value,
  ghosts: ghostCount.value,
  extraPies: extraPies.value,
  speed: speed.value,
});

/**
 * Best scores are keyed by difficulty AND setup: fifty Rafs on the small board
 * is not the same game as one Raf on the standard board, and putting both in
 * one column makes the number meaningless.
 */
export const scoreKey = (id = difficulty.value) => (
  `${id}|${boardId.value}|${ghostCount.value}|${extraPies.value}|${speed.value}`
);
export const bestFor = (key) => best.value[key] ?? { score: 0, level: 0 };

/** Returns true when this run beat the previous best, so the UI can say so. */
export function recordRun(key, score, level) {
  const prev = bestFor(key);
  const beaten = score > prev.score;
  best.value = {
    ...best.value,
    [key]: { score: Math.max(prev.score, score), level: Math.max(prev.level, level) },
  };
  return beaten;
}

/** Every setup that has ever been played, newest-scoring first. */
export function playedSetups() {
  return Object.entries(best.value)
    .map(([k, v]) => {
      const [diff, board, ghosts, pies, pace] = k.split('|');
      return {
        key: k, ...v, difficulty: difficultyById(diff).name, board: boardById(board).name,
        ghosts: Number(ghosts) || 1, pies: Number(pies) || 0,
        // Older keys have no pace segment; they were all played at Normal.
        speed: speedById(pace ?? 'normal').name,
      };
    })
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score);
}

export const currentDifficulty = () => difficultyById(difficulty.value);
export const currentSpeed = () => speedById(speed.value);
